import { useCallback, useEffect, useState } from "react";
import { Check, Search, UserCheck, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  pendingPortalUsersService,
  PendingPortalUserRow,
} from "@/services/pendingPortalUsers.service/pendingPortalUsers.api";

function formatDate(iso?: string) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
}

export default function PendingApprovals() {
  const [pendingUsers, setPendingUsers] = useState<PendingPortalUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actingId, setActingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [res] = await pendingPortalUsersService.list({ limit: 100, page: 1 });
    if (res?.result) setPendingUsers(res.result);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredUsers = pendingUsers.filter(
    (user) =>
      user.name?.toLowerCase().includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase()) ||
      user.clientName?.toLowerCase().includes(search.toLowerCase())
  );

  const handleApprove = async (id: string) => {
    setActingId(id);
    const [, err] = await pendingPortalUsersService.approve(id);
    setActingId(null);
    if (err) {
      toast.error(err.msg || "Erro ao aprovar");
      return;
    }
    toast.success("Usuário aprovado com sucesso!");
    load();
  };

  const handleReject = async (id: string) => {
    setActingId(id);
    const [, err] = await pendingPortalUsersService.reject(id);
    setActingId(null);
    if (err) {
      toast.error(err.msg || "Erro ao rejeitar");
      return;
    }
    toast.success("Cadastro rejeitado.");
    load();
  };

  return (
    <div className="space-y-6">
      <Card className="p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar por nome ou e-mail..."
            className="pl-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Cliente (CRM)</TableHead>
              <TableHead>Data da solicitação</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Carregando…
                </TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2">
                    <UserCheck className="h-8 w-8 text-muted-foreground" />
                    <p className="text-muted-foreground">Nenhuma solicitação pendente encontrada</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user._id}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      {user.email}
                    </div>
                  </TableCell>
                  <TableCell>{user.clientName || user.clientEmail || "—"}</TableCell>
                  <TableCell>{formatDate(user.createdAt)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={actingId === user._id}
                        onClick={() => handleReject(user._id)}
                      >
                        Rejeitar
                      </Button>
                      <Button
                        size="sm"
                        disabled={actingId === user._id}
                        onClick={() => handleApprove(user._id)}
                        className="gap-1"
                      >
                        <Check className="h-4 w-4" />
                        Aprovar
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
